import { useMemo, useState } from 'react';
import { featuredStories } from '@/mocks/featured';
import type { StoryCard } from '@/pages/home/types';

const stories: StoryCard[] = featuredStories;

const FILTERS = ['全部', '明星绯闻', '网红黑料', '吃瓜现场', '演出动态', '片场花絮'];

export default function Featured() {
  const [activeFilter, setActiveFilter] = useState('全部');
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return stories.filter((story) => {
      const matchTag = activeFilter === '全部' || story.category === activeFilter;
      const matchKw =
        kw === '' ||
        story.title.toLowerCase().includes(kw) ||
        story.summary.toLowerCase().includes(kw) ||
        story.tags.some((t) => t.toLowerCase().includes(kw));
      return matchTag && matchKw;
    });
  }, [activeFilter, keyword]);

  return (
    <section id="featured" className="w-full px-4 md:px-8 py-14 md:py-20 scroll-mt-20 bg-background-50">
      <div className="mb-8 md:mb-10 max-w-2xl">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-accent-800 bg-accent-100 px-3 py-1.5 rounded-full mb-4">
          <i className="ri-search-line"></i> 吃瓜精选
        </span>
        <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug mb-3">
          按频道筛选，找你想看的那条
        </h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          选一个频道标签，或直接输入关键词。筛选与搜索仅作用于本页展示的内容，切换即时生效。
        </p>
      </div>

      <div className="flex flex-col gap-4 mb-6 md:mb-8">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((filter) => {
            const active = activeFilter === filter;
            return (
              <button
                key={filter}
                type="button"
                onClick={() => setActiveFilter(filter)}
                aria-pressed={active}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  active
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {filter}
              </button>
            );
          })}
        </div>

        <div className="relative w-full md:max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索标题、摘要或标签，例如：番位、巡演"
            className="w-full pl-10 pr-10 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword('')}
              aria-label="清空搜索"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 cursor-pointer"
            >
              <i className="ri-close-circle-line"></i>
            </button>
          )}
        </div>

        <p className="text-xs text-foreground-500">
          共找到 <strong className="text-primary-600">{filtered.length}</strong> 条内容
        </p>
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {filtered.map((story) => (
            <article
              key={story.id}
              className="group flex flex-col rounded-lg overflow-hidden bg-background-100 border border-background-200 hover:border-primary-300 transition-colors"
            >
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src={story.image}
                  alt={story.title}
                  title={`${story.title} · 爆料网`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-900 text-xs font-bold">
                  {story.category}
                </span>
              </div>
              <div className="p-4 md:p-5 flex-1 flex flex-col">
                <span className="text-xs text-foreground-500 mb-2">
                  <i className="ri-time-line mr-1"></i>
                  {story.time}
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug mb-2 group-hover:text-primary-700 transition-colors">
                  {story.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed flex-1">{story.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {story.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 rounded-lg bg-background-100 border border-dashed border-background-300 text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </span>
          <p className="font-heading font-bold text-foreground-900 mb-1">没有找到匹配的内容</p>
          <p className="text-sm text-foreground-600 mb-4">试试换个关键词，或选择“全部”频道</p>
          <button
            type="button"
            onClick={() => {
              setActiveFilter('全部');
              setKeyword('');
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      )}
    </section>
  );
}